/**
 * The World's reminders in settings (RFC 0350 S3): what each says, when it is
 * next due, and pause, resume or cancel. Agents set them with
 * `create_reminder`; this is where the person sees and stops them.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  projectSchedule,
  type ScheduleDirectoryRef,
  type ScheduleState,
} from '@mod/core/model/schedules'
import { useMemo } from 'react'
import { useDocument } from '../../services/world/repo/useDocument'
import { useWorldReminders } from '../../services/world/useWorldReminders'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function nextLabel(iso: string | undefined): string {
  if (!iso) return 'Nothing due'
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function ReminderRow({
  entry,
  onSetState,
}: {
  entry: ScheduleDirectoryRef
  onSetState: (scheduleId: string, state: ScheduleState) => void
}) {
  const [doc] = useDocument(entry.scheduleId as AnyDocumentId)
  const schedule = useMemo(
    () => projectSchedule(entry.scheduleId, doc),
    [entry.scheduleId, doc],
  )
  const rule = !schedule
    ? ''
    : schedule.timing.kind === 'once'
      ? 'Once'
      : schedule.timing.rule.every === 'day'
        ? `Daily ${schedule.timing.rule.time}`
        : `${(schedule.timing.rule.weekdays ?? []).map((d) => DAYS[d]).join(', ')} ${schedule.timing.rule.time}`
  const last = schedule?.recent[schedule.recent.length - 1]
  const paused = entry.state === 'paused'
  return (
    <li className="rounded border border-border/60 px-2 py-1.5 text-[12px]">
      <div className="text-foreground">{schedule?.target.text ?? '…'}</div>
      <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[11px] text-muted-foreground">
        <span>
          {rule}
          {' · '}
          {paused ? 'Paused' : nextLabel(entry.nextAt)}
          {last && last.status !== 'claimed' && last.status !== 'delivered'
            ? ` · last ${last.status}${last.note ? `: ${last.note}` : ''}`
            : ''}
        </span>
        <span className="flex shrink-0 gap-2">
          <button
            type="button"
            className="hover:text-foreground"
            onClick={() =>
              onSetState(entry.scheduleId, paused ? 'active' : 'paused')
            }
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button
            type="button"
            className="hover:text-foreground"
            onClick={() => onSetState(entry.scheduleId, 'cancelled')}
          >
            Cancel
          </button>
        </span>
      </div>
    </li>
  )
}

export function RemindersList() {
  const { reminders, setState } = useWorldReminders()
  if (reminders.length === 0) {
    return (
      <p className="text-[12px] text-muted-foreground">
        None. Ask an agent to remind you of something.
      </p>
    )
  }
  return (
    <ul className="space-y-1">
      {reminders.map((r) => (
        <ReminderRow key={r.scheduleId} entry={r} onSetState={setState} />
      ))}
    </ul>
  )
}
